'use client';

import { useEffect, useRef } from 'react';
import { useTranslations } from '../hooks/use-translations';
import { LanguageSelector } from '../components/languageSelector';
import './Sonos2025landing.css';

export function SonosSoundsuitContent() {
  const { t, language } = useTranslations();
  const albumRef = useRef<HTMLImageElement | null>(null);
  const speakerRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const elements: Array<HTMLElement> = [];
    if (albumRef.current) elements.push(albumRef.current);
    if (speakerRef.current) elements.push(speakerRef.current);

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealIn');
          } else {
            entry.target.classList.remove('revealIn');
          }
        });
      },
      { threshold: 0.2 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const steps = [t('step1'), t('step2')];

  return (
    <div className='page'>
      <LanguageSelector />

      {/* Header Section */}
      <div className='header'>
        {/* Logo */}
        <div className='logo'>
          <a href='https://www.sonos.com/' target='_blank' rel='noopener noreferrer'>
            <img src='/sonos-logo.svg' alt='Sonos logo' className='sonosLogo' />
          </a>
          <span className='logoSep'> | </span>
          <a href='https://soundsuit.fm/' target='_blank' rel='noopener noreferrer'>
            <img src='/soundsuit.svg' alt='Soundsuit logo' className='soundsuitLogo' />
          </a>
        </div>

        {/* Album Image */}
        <img
          ref={albumRef}
          src='/covers.png'
          alt='Album covers showcasing Soundsuit music library'
          className='albumImage revealBase'
        />

        {/* Overline */}
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <p className='overline'>{t('exclusiveLine')}</p>

          {/* Main Heading */}
          <h1 className='mainHeading'>{t('legalStreamingHeadline')}</h1>
        </div>

        {/* CTAs */}
        <div className='ctaGroup'>
          <a
            className='ctaOutline'
            href={`https://sonos-distributors.info/`}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={`${t('partnerButton')} – ${t('legalStreamingHeadline')}`}
          >
            {t('partnerButton')}
          </a>
          <a
            className='cta'
            href={`https://account.soundsuit.fm/signin-sonos?language=${language}`}
            aria-label={`${t('testNowButton')} – ${t('legalStreamingHeadline')}`}
          >
            {t('testNowButton')}
          </a>
        </div>

        {/* Speaker Image Placeholder */}
        <figure className='speakerWrap'>
          <img
            ref={speakerRef}
            src='/sonos-device.webp'
            alt='Sonos Era 100 Pro speaker for commercial use'
            className='speakerImgCropped revealBase'
          />
        </figure>
      </div>

      {/* Mid Info Section ("Did you know?") */}
      <section className='infoHero'>
        <div className='infoHeroInner'>
          <h2 className='infoTitle'>{t('didYouKnow')}</h2>
          <p className='infoText'>{t('didYouKnowText')}</p>
        </div>
      </section>

      {/* Bottom Section */}
      <div className='bottom'>
        <div className='container' style={{ maxWidth: '900px' }}>
          {/* Features Section (alternating rows with media placeholders) */}
          <section>
            <div className='featureRows'>
              {/* Row 1: Text left, media right */}
              <div className='featureRow'>
                <div className='featureCopy'>
                  <h2 className='featureTitleLg'>{t('threeMonthsFree')}</h2>
                  <p className='featureText'>{t('threeMonthsText')}</p>
                </div>
                <div className='featureMedia' aria-hidden='true'>
                  <img
                    src='/img1.jpg'
                    alt=''
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '12px',
                    }}
                  />
                </div>
              </div>

              {/* Row 2: Media left, text right */}
              <div className='featureRow featureRow--reverse'>
                <div className='featureMedia' aria-hidden='true'>
                  <img
                    src='/img2.jpg'
                    alt=''
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '12px',
                    }}
                  />
                </div>
                <div className='featureCopy'>
                  <h3 className='featureTitle'>{t('millionsLicensed')}</h3>
                  <p className='featureText'>{t('millionsLicensedText')}</p>
                </div>
              </div>

              {/* Row 3: Text left, media right */}
              <div className='featureRow'>
                <div className='featureCopy'>
                  <h3 className='featureTitle'>{t('easyControl')}</h3>
                  <p className='featureText'>{t('easyControlText')}</p>
                </div>
                <div className='featureMedia' aria-hidden='true'>
                  <img
                    src='/img3.jpg'
                    alt=''
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '12px',
                    }}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Second CTA */}
          {/* <div className='ctaDarkWrap'>
            <a
              className='ctaDark'
              href={`https://account.soundsuit.fm/signin-sonos?language=${language}`}
              aria-label={`${t('signUpButton')} – ${t('legalStreamingHeadline')}`}
            >
              {t('signUpButton')}
            </a>
          </div> */}
        </div>
      </div>

      {/* Steps Section */}
      <div className='steps'>
        <h2 className='stepsTitle'>{t('whatMustYouDo')}</h2>

        <div style={{ maxWidth: '650px', margin: '40px auto', paddingLeft: 16, paddingRight: 16 }}>
          <div className='stepsList'>
            {steps.map((step, index) => (
              <div key={index} className='stepItem'>
                <div className='stepIndex'>{index + 1}</div>
                <p className='stepText'>{step}</p>
              </div>
            ))}
          </div>
        </div>

        <div className='ctaGroup'>
          <a
            className='ctaOutline'
            href={`https://sonos-distributors.info/`}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={`${t('partnerButton')} – ${t('whatMustYouDo')}`}
          >
            {t('partnerButton')}
          </a>
          <a
            className='cta'
            href={`https://account.soundsuit.fm/signin-sonos?language=${language}`}
            aria-label={`${t('testNowButton')} – ${t('whatMustYouDo')}`}
          >
            {t('testNowButton')}
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className='siteFooter'>
        <div className='siteFooterInner'>
          <p className='footerText'>
            * {t('noHiddenConditionsText')} {t('offerValid')} {t('codesValid')}
          </p>
          <p className='footerCopy'>© 2025 Sonos, Inc.</p>
        </div>
      </footer>
    </div>
  );
}
